import moment, { Moment } from 'moment';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { Alert } from 'reactstrap';
import styled from 'styled-components';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import {
  createIssuerOrganization,
  createOrganizationApplicationSetting,
  getIssuerOrganization,
  getOrganizationApplicationSettingOrNull,
  updateIssuerOrganization,
} from '~/javascripts/admin/actions/organization';
import { useAppDispatch } from '~/javascripts/admin/hooks/useApp';
import { testEmailAllowedLetters, validateEmail } from '~/javascripts/admin/utils/email';
import { Button } from '../../atoms/Buttons';
import CheckBox from '../../atoms/CheckBox';
import DateTimeRange from '../../atoms/DateTimeRange';
import Grid from '../../atoms/Grid';
import Headings from '../../atoms/Headings';
import Label from '../../atoms/Label';
import Radio from '../../atoms/Radio';
import Required from '../../atoms/Required';
import TextBox from '../../atoms/TextBox';
import Tooltip from '../../atoms/Tooltip';
import BreadCrumb from '../../molecules/BreadCrumb';
import { FormPreview } from '../../shared/IssueForms';

const getCrumbs = (mode: 'create' | 'edit') => [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: 'サービス管理', href: '', isCurrentPage: true },
  { value: '発行体管理', href: '/service-settings/issuer-organization', isCurrentPage: false },
  { value: mode === 'create' ? '発行体新規作成' : '発行体詳細', href: '', isCurrentPage: true },
];

type FormValues = {
  code: string;
  name: string;
  invoice_registration_number: string;
  contract_range: { start_date: Moment | null; end_date: Moment | null };
  contact_post: string;
  contact_name: string;
  contact_email: string;
  admin_screen: boolean;
  pokepay_app: boolean;
  mobile_sdk: boolean;
  partner_sdk: boolean;
  merchant_application: boolean;
  member_organization_code_prefix: string;
};

type APIError = {
  type: string;
  message: string;
  errors?: {
    invalid?: string[];
  };
};

type APIFieldError = {
  key: keyof FormValues | 'generic';
  error: {
    type: string;
    message: string;
  };
};

const FormGrid = styled(Grid.Container)`
  ${Label} {
    margin: 0;
    padding: 5px 0;
  }

  ${DateTimeRange} {
    flex: 1;
    > span {
      padding: 0 4px;
      line-height: 32px;
    }

    > .rdt {
      flex: 1;
    }
  }
`;

const Prefixed = styled.div`
  position: relative;
  width: 100%;
`;

const InputPrefix = styled.span`
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  padding: 0 8px;
`;

const InputError = styled.div`
  grid-column: 2;
  color: red;
`;

const RadioWrapper = styled.div`
  display: flex;
  align-items: center;
  ${Label} {
    padding: 0px;
  }
  ${Radio}, ${CheckBox} {
    margin-right: 8px;
  }
`;

const Disabler = styled.div<{ disabled: boolean }>`
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const API_ERRORS = {
  organization_conflict: {
    key: 'code',
    error: {
      type: 'organization_conflict',
      message: 'この組織コードは既に登録されています',
    },
  },
  organization_application_setting_prefix_conflict: {
    key: 'member_organization_code_prefix',
    error: {
      type: 'organization_application_setting_prefix_conflict',
      message: 'このプレフィックスはすでに使用されています',
    },
  },
  organization_application_setting_already_exists: {
    key: 'member_organization_code_prefix',
    error: {
      type: 'organization_application_setting_already_exists',
      message: '既にプレフィックスが登録されています',
    },
  },
  generic: {
    key: 'generic',
    error: {
      type: 'generic',
      message: 'エラーが発生しました',
    },
  },
};

const getAPIFieldError = (error: APIError): APIFieldError => {
  if (error?.type === 'invalid_parameters') {
    if (error?.errors?.invalid?.indexOf('code') !== -1) {
      return {
        key: 'code',
        error: {
          type: 'invalid_parameters',
          message:
            '組織コードは半角のアルファベット、数字、- （ハイフン）から成る32文字以下の文字列である必要があります',
        },
      };
    }
    if (
      error?.errors?.invalid?.indexOf('start_date') !== -1 ||
      error?.errors?.invalid?.indexOf('end_date') !== -1
    ) {
      return {
        key: 'contract_range',
        error: {
          type: 'invalid_parameters',
          message: '期間の指定が正しくありません',
        },
      };
    }
    if (error?.errors?.invalid?.indexOf('member_organization_code_prefix') !== -1) {
      return {
        key: 'member_organization_code_prefix',
        error: {
          type: 'invalid_parameters',
          message: 'プレフィックスは半角英数字 2〜10 文字で指定してください',
        },
      };
    }
  }

  return API_ERRORS[error?.type] ?? API_ERRORS.generic;
};

const EditIssuerOrganization = () => {
  const { id } = useParams<{ id: string }>();
  const [success, setSuccess] = React.useState(false);
  const dispatch = useAppDispatch();
  const mode = !id ? 'create' : 'edit';

  // edit モードのとき、既に prefix が設定済みかどうか。
  // 設定済みの場合 prefix フィールドは read-only。
  // 未設定の場合のみ 1 回だけ入力 + POST を許可する。
  const [existingPrefix, setExistingPrefix] = React.useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      code: '',
      name: '',
      invoice_registration_number: '',
      contract_range: {
        start_date: null,
        end_date: null,
      },
      contact_post: '',
      contact_name: '',
      contact_email: '',
      admin_screen: true,
      pokepay_app: true,
      mobile_sdk: false,
      partner_sdk: false,
      merchant_application: false,
      member_organization_code_prefix: '',
    },
  });

  React.useEffect(() => {
    setSuccess(false);
    setExistingPrefix(null);
    if (!id) return;

    // 発行体本体と prefix 設定を並列取得。prefix 取得は副次情報なので、
    // どんなエラーでも null にフォールバックし発行体本体の表示をブロックしない。
    // (reset 後に setValue する形だと両者の resolve 順序で race condition が
    //  起きるため、両 Promise を待ってから一度だけ reset する。)
    const prefixPromise = getOrganizationApplicationSettingOrNull(id).catch(() => null);

    Promise.all([getIssuerOrganization(id), prefixPromise]).then(([response, setting]) => {
      const prefix = setting?.member_organization_code_prefix ?? '';
      setExistingPrefix(prefix || null);
      reset({
        code: response.code ?? '',
        name: response.name ?? '',
        invoice_registration_number: response.invoice_registration_number?.slice(1) ?? '',
        contract_range: {
          start_date: response.start_date ? moment(response.start_date) : null,
          end_date: response.end_date ? moment(response.end_date) : null,
        },
        contact_post: response.contact_post ?? '',
        contact_name: response.contact_name ?? '',
        contact_email: response.contact_email ?? '',
        admin_screen: response.admin_screen ?? false,
        pokepay_app: response.pokepay_app ?? false,
        mobile_sdk: response.mobile_sdk ?? false,
        partner_sdk: response.partner_sdk ?? false,
        merchant_application: response.display_features?.merchant_application ?? false,
        member_organization_code_prefix: prefix,
      });
    });
  }, [id, reset]);

  // prefix を編集可能か (= API で書き込みできるか) を判定
  // - create モード: 常に編集可
  // - edit モード: 既存設定がない場合のみ編集可 (1 回だけ create 可能)
  const isPrefixEditable = mode === 'create' || existingPrefix === null;

  const onConfirm = (data: FormValues) => {
    const { code, name, invoice_registration_number } = data;
    const prefixInput = data.member_organization_code_prefix?.trim() ?? '';
    const shouldCreatePrefix = isPrefixEditable && prefixInput.length > 0;

    const handleApiError = (err: APIError) => {
      const apiFieldError = getAPIFieldError(err);
      if (apiFieldError.key !== 'generic') {
        setError(apiFieldError.key, {
          type: apiFieldError.error.type,
          message: apiFieldError.error.message,
        });
        return;
      }
      dispatch(
        openAlertModal({
          body: apiFieldError.error.message,
          useCancel: false,
        })
      );
    };

    dispatch(
      openAlertModal({
        body: (
          <FormPreview
            message="以下の内容で登録してよろしいですか？"
            columns={[
              {
                title: '組織コード',
                ref: () => code,
              },
              {
                title: '組織名',
                ref: () => name,
              },
              {
                title: '登録番号',
                ref: () => (invoice_registration_number ? `T${invoice_registration_number}` : '-'),
              },
              {
                title: '契約期間',
                ref: () =>
                  `${data.contract_range.start_date?.format('YYYY/MM/DD') ?? '-'} ~ ${
                    data.contract_range.end_date?.format('YYYY/MM/DD') ?? '-'
                  }`,
              },
              ...(isPrefixEditable
                ? [
                    {
                      title: '組織コードのプレフィックス設定',
                      ref: () => prefixInput || '-',
                    },
                  ]
                : []),
              // --- 連絡先 ---
              {
                title: '連絡先 / 担当部署・役職',
                ref: () => data.contact_post || '-',
              },
              {
                title: '連絡先 / 氏名',
                ref: () => data.contact_name || '-',
              },
              {
                title: '連絡先 / メールアドレス',
                ref: () => data.contact_email || '-',
              },
              // --- 基本サービス ---
              {
                title: '基本サービス / Pokepay管理画面',
                ref: () => (data.admin_screen ? '利用あり' : '利用なし'),
              },
              {
                title: '基本サービス / 提供アプリ',
                ref: () => (data.pokepay_app ? 'Pokepayアプリ' : '発行体アプリ'),
              },
              {
                title: '基本サービス / 提供SDK',
                ref: () =>
                  data.pokepay_app
                    ? '-'
                    : [data.mobile_sdk && 'MobileSDK', data.partner_sdk && 'PartnerSDK']
                        .filter(Boolean)
                        .join(' / ') || 'なし',
              },
              // --- オプション機能 利用設定 ---
              {
                title: 'オプション機能 / 加盟店申込フロー',
                ref: () => (data.merchant_application ? '利用する（表示）' : '利用しない（非表示）'),
              },
            ]}
          />
        ),
        onConfirm: () =>
          (mode === 'create'
            ? createIssuerOrganization({
                code: data.code,
                name: data.name,
                ...(data.invoice_registration_number
                  ? {
                      invoice_registration_number: data.invoice_registration_number,
                    }
                  : {}),
                ...(data.contract_range.start_date
                  ? { start_date: data.contract_range.start_date.toISOString() }
                  : {}),
                ...(data.contract_range.end_date
                  ? { end_date: data.contract_range.end_date.toISOString() }
                  : {}),
                ...(data.contact_post ? { contact_post: data.contact_post } : {}),
                ...(data.contact_name ? { contact_name: data.contact_name } : {}),
                ...(data.contact_email ? { contact_email: data.contact_email } : {}),
                admin_screen: data.admin_screen,
                pokepay_app: data.pokepay_app,
                mobile_sdk: data.mobile_sdk,
                partner_sdk: data.partner_sdk,
                display_features: { merchant_application: data.merchant_application },
              })
            : updateIssuerOrganization(data.code, {
                name: data.name,
                invoice_registration_number: data.invoice_registration_number
                  ? data.invoice_registration_number
                  : null,
                ...(data.contract_range.start_date
                  ? { start_date: data.contract_range.start_date.toISOString() }
                  : {}),
                ...(data.contract_range.end_date
                  ? { end_date: data.contract_range.end_date.toISOString() }
                  : {}),
                contact_post: data.contact_post ? data.contact_post : null,
                contact_name: data.contact_name ? data.contact_name : null,
                contact_email: data.contact_email ? data.contact_email : null,
                admin_screen: data.admin_screen,
                pokepay_app: data.pokepay_app,
                mobile_sdk: data.mobile_sdk,
                partner_sdk: data.partner_sdk,
                display_features: { merchant_application: data.merchant_application },
              })
          )
            .then(() => {
              if (!shouldCreatePrefix) {
                setSuccess(true);
                return undefined;
              }
              return createOrganizationApplicationSetting({
                member_organization_code_prefix: prefixInput,
                issuer_organization_code: data.code,
              })
                .then(() => {
                  setExistingPrefix(prefixInput);
                  setSuccess(true);
                })
                .catch((e) =>
                  Promise.resolve(e).then((err) => {
                    if (!err?.type) return;
                    // 発行体本体は成功しているので、success Alert は出したまま prefix 失敗のみを伝える。
                    setSuccess(true);
                    const apiFieldError = getAPIFieldError(err);
                    if (apiFieldError.key !== 'generic') {
                      setError(apiFieldError.key, {
                        type: apiFieldError.error.type,
                        message: apiFieldError.error.message,
                      });
                    }
                    dispatch(
                      openAlertModal({
                        body:
                          mode === 'create'
                            ? '発行体は作成されましたが、プレフィックスの登録に失敗しました。発行体詳細画面から再度設定してください。'
                            : 'プレフィックスの登録に失敗しました。',
                        useCancel: false,
                      })
                    );
                  })
                );
            })
            .catch((e) =>
              Promise.resolve(e).then((err) => {
                if (err?.type) {
                  handleApiError(err);
                }
              })
            ),
      })
    );
  };

  return (
    <>
      <BreadCrumb crumbs={getCrumbs(mode)} />
      <Alert color="success" isOpen={success}>
        {mode === 'create' ? '発行体の作成に成功しました' : '発行体の更新に成功しました'}
      </Alert>
      <Headings.h4>{mode === 'create' ? '発行体新規作成' : '発行体詳細'}</Headings.h4>
      <form onSubmit={handleSubmit(onConfirm)}>
        <FormGrid rowGap={8}>
          <Grid.Item size={2}>
            <Label>
              組織コード
              <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={4}>
            <Controller
              control={control}
              name="code"
              rules={{ required: true }}
              render={({ field: { value, onChange, ...rest } }) =>
                mode === 'create' ? (
                  <TextBox
                    fullWidth
                    required
                    syncValue={value}
                    maxLength={32}
                    onChange={(e) => {
                      if (/^[a-zA-Z0-9-]+$/.test(e.target.value) || e.target.value === '') {
                        onChange(e);
                      }
                    }}
                    {...rest}
                  />
                ) : (
                  <Label>{value}</Label>
                )
              }
            />
          </Grid.Item>
          {errors.code && (
            <Grid.Item startColumn={3} size={4}>
              <InputError>{errors.code.message}</InputError>
            </Grid.Item>
          )}

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>
              組織名
              <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={4}>
            <TextBox fullWidth {...register('name', { required: true })} required maxLength={256} />
          </Grid.Item>
          {errors.name && (
            <Grid.Item startColumn={3} size={4}>
              <InputError>{errors.name.message}</InputError>
            </Grid.Item>
          )}

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>登録番号</Label>
          </Grid.Item>
          <Grid.Item size={4}>
            <Prefixed>
              <InputPrefix>T</InputPrefix>
              <TextBox
                fullWidth
                {...register('invoice_registration_number', {
                  pattern: {
                    value: /^(\d{13})?$/,
                    message: '13桁の半角数字を指定して下さい',
                  },
                })}
                placeholder="13桁の半角数字を指定して下さい"
              />
            </Prefixed>
          </Grid.Item>
          {errors.invoice_registration_number && (
            <Grid item startColumn={3} size={4}>
              <InputError>{errors.invoice_registration_number.message}</InputError>
            </Grid>
          )}

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>決済加盟店設定</Label>
          </Grid.Item>
          <Grid.Item size={4} flexDirection="column">
            <Label>
              組織コードのプレフィックス設定
              <Tooltip
                id="helpMemberOrganizationCodePrefix"
                text="決済加盟店の組織コードに使用するプレフィックスを指定します。半角英数字 2〜10 文字までです"
              />
            </Label>
            <Controller
              control={control}
              name="member_organization_code_prefix"
              rules={{
                pattern: {
                  value: /^([a-zA-Z0-9]{2,10})?$/,
                  message: 'プレフィックスは半角英数字 2〜10 文字で入力してください',
                },
              }}
              render={({ field: { value, onChange, ...rest } }) =>
                isPrefixEditable ? (
                  <TextBox
                    fullWidth
                    syncValue={value}
                    maxLength={10}
                    onChange={(e) => {
                      if (/^[a-zA-Z0-9]*$/.test(e.target.value)) {
                        onChange(e);
                      }
                    }}
                    {...rest}
                  />
                ) : (
                  <Label>{value || '-'}</Label>
                )
              }
            />
            {errors.member_organization_code_prefix && (
              <InputError>{errors.member_organization_code_prefix.message}</InputError>
            )}
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>契約期間</Label>
          </Grid.Item>
          <Grid.Item size={4}>
            <Controller
              name="contract_range"
              control={control}
              rules={{
                validate: (value) =>
                  value.end_date?.isBefore(value.start_date)
                    ? '期間の指定が正しくありません'
                    : true,
              }}
              render={({ field: { value, onChange } }) => (
                <DateTimeRange
                  dateRange={{
                    from: value.start_date,
                    to: value.end_date,
                  }}
                  setDateRange={(range) => {
                    onChange({
                      start_date: range.from ?? null,
                      end_date: range.to ?? null,
                    });
                  }}
                  dateTimeProps={{
                    fromProps: {
                      inputProps: {
                        readOnly: true,
                      },
                      timeFormat: false,
                    },
                    toProps: {
                      inputProps: {
                        readOnly: true,
                      },
                      timeFormat: false,
                    },
                  }}
                />
              )}
            />
          </Grid.Item>
          {errors.contract_range && (
            <Grid.Item startColumn={3} size={4}>
              <InputError>期間の指定が正しくありません</InputError>
            </Grid.Item>
          )}

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>連絡先</Label>
          </Grid.Item>
          <Grid.Item size={4} flexDirection="column">
            <Label>担当部署/役職</Label>
            <TextBox
              fullWidth
              {...register('contact_post', { maxLength: 32 })}
              maxLength={32}
              placeholder="例）マーケティング本部　部長"
            />
            <Label>氏名</Label>
            <TextBox
              fullWidth
              {...register('contact_name', { maxLength: 32 })}
              maxLength={32}
              placeholder="例）山田太郎"
            />
            <Label>メールアドレス</Label>
            <Controller
              control={control}
              name="contact_email"
              rules={{
                maxLength: 256,
                validate: {
                  invalidEmail: (value) => validateEmail(value),
                },
              }}
              render={({ field: { value, onChange } }) => (
                <TextBox
                  fullWidth
                  maxLength={256}
                  placeholder="例）pokepay@example.com"
                  syncValue={value}
                  onChange={(e) =>
                    testEmailAllowedLetters(e.target.value) && onChange(e.target.value)
                  }
                />
              )}
            />
            <InputError>{errors.contact_email?.message}</InputError>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>基本サービス</Label>
          </Grid.Item>

          <Grid.Item container size={4} gridSize={2}>
            <Grid.Item flexDirection="column">
              <Label>Pokepay管理画面</Label>
              <Controller
                control={control}
                name="admin_screen"
                render={({ field: { value, onChange } }) => (
                  <>
                    <RadioWrapper>
                      <Radio
                        id="admin_screen_true"
                        checked={value}
                        onChange={() => onChange(true)}
                      />
                      <Label htmlFor="admin_screen_true">利用あり</Label>
                    </RadioWrapper>
                    <RadioWrapper>
                      <Radio
                        id="admin_screen_false"
                        checked={!value}
                        onChange={() => onChange(false)}
                      />
                      <Label htmlFor="admin_screen_false">利用なし</Label>
                    </RadioWrapper>
                  </>
                )}
              />
            </Grid.Item>

            <Grid.Spacer />

            <Grid.Item flexDirection="column" nextRow>
              <Label>提供アプリ</Label>
              <Controller
                control={control}
                name="pokepay_app"
                render={({ field: { value, onChange } }) => (
                  <>
                    <RadioWrapper>
                      <Radio
                        id="pokepay_app_radio_true"
                        checked={value}
                        onChange={() => {
                          onChange(true);
                          setValue('mobile_sdk', false);
                          setValue('partner_sdk', false);
                        }}
                      />
                      <Label htmlFor="pokepay_app_radio_true">Pokepayアプリ</Label>
                    </RadioWrapper>
                    <RadioWrapper>
                      <Radio
                        id="pokepay_app_radio_false"
                        checked={!value}
                        onChange={() => onChange(false)}
                      />
                      <Label htmlFor="pokepay_app_radio_false">発行体アプリ</Label>
                    </RadioWrapper>
                  </>
                )}
              />
            </Grid.Item>

            <Grid.Item flexDirection="column">
              <Disabler disabled={watch('pokepay_app')}>
                <Label>提供SDK</Label>
                <RadioWrapper>
                  <Controller
                    control={control}
                    name="mobile_sdk"
                    render={({ field: { value, onChange } }) => (
                      <CheckBox
                        id="mobile_sdk_check"
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    )}
                  />
                  <Label htmlFor="mobile_sdk_check">MobileSDK</Label>
                </RadioWrapper>
                <RadioWrapper>
                  <Controller
                    control={control}
                    name="partner_sdk"
                    render={({ field: { value, onChange } }) => (
                      <CheckBox
                        id="partner_sdk_check"
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    )}
                  />
                  <Label htmlFor="partner_sdk_check">PartnerSDK</Label>
                </RadioWrapper>
              </Disabler>
            </Grid.Item>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>
              オプション機能
              <br />
              利用設定
            </Label>
          </Grid.Item>

          <Grid.Item size={4} flexDirection="column">
            <Label>加盟店申込フロー（申込・審査・承認）</Label>
            <Controller
              control={control}
              name="merchant_application"
              render={({ field: { value, onChange } }) => (
                <div role="radiogroup" aria-label="加盟店申込フロー（申込・審査・承認）">
                  <RadioWrapper>
                    <Radio
                      id="merchant_application_true"
                      checked={value}
                      onChange={() => onChange(true)}
                    />
                    <Label htmlFor="merchant_application_true">利用する（表示）</Label>
                  </RadioWrapper>
                  <RadioWrapper>
                    <Radio
                      id="merchant_application_false"
                      checked={!value}
                      onChange={() => onChange(false)}
                    />
                    <Label htmlFor="merchant_application_false">利用しない（非表示）</Label>
                  </RadioWrapper>
                </div>
              )}
            />
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item>
            <Button type="submit">{mode === 'create' ? '登録' : '更新'}</Button>
          </Grid.Item>
        </FormGrid>
      </form>
    </>
  );
};

export default EditIssuerOrganization;
